import { useEffect, useState } from "react";
import axios from "axios";
import { AboutSubnav, AboutHero } from "../../components/AboutLayout";
import Reveal from "../../components/Reveal";
import TeamMemberModal from "../../components/TeamMemberModal";
import { Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EditorialSplit — premium two-column editorial layout used for
 * Purpose / Values / Vision / Mission sections.
 * Left:  large bold statement (display font, uppercase).
 * Right: supporting descriptive content.
 */
const EditorialSplit = ({
  kicker,
  headlineTop,
  headlineAccent,
  paragraphs = [],
  tone = "light", // light | offwhite | dark
  testId,
  index,
}) => {
  const isDark = tone === "dark";
  const bg = tone === "dark" ? "bg-[#1c1c1e]" : tone === "offwhite" ? "bg-[#f7f7f5]" : "bg-white";
  const textMuted = isDark ? "text-white/70" : "text-[#4a4a4e]";
  const textHead = isDark ? "text-white" : "text-[#1c1c1e]";
  return (
    <section
      data-testid={testId}
      className={`relative border-b border-black/5 py-24 md:py-32 ${bg}`}
    >
      {isDark && (
        <>
          <div className="absolute inset-0 bg-grid opacity-10" />
          <div className="absolute -right-40 top-1/2 h-[520px] w-[520px] -translate-y-1/2 rounded-full bg-[#e85a1c]/20 blur-3xl" />
        </>
      )}
      <div className="relative mx-auto grid max-w-[1400px] gap-y-10 gap-x-16 px-6 md:grid-cols-12 lg:px-10">
        <Reveal className="md:col-span-5 md:sticky md:top-32 md:self-start">
          <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
            {kicker}
          </div>
          <h2
            className={`mt-6 font-display text-[12vw] uppercase leading-[0.82] tracking-tighter md:text-[6.5vw] lg:text-[88px] ${textHead}`}
          >
            {headlineTop}
            <br />
            <span className="text-[#e85a1c]">{headlineAccent}</span>
          </h2>
          {typeof index === "number" && (
            <div className={`mt-8 font-mono-industrial text-[10px] uppercase tracking-[0.3em] ${isDark ? "text-white/40" : "text-[#bababd]"}`}>
              · {String(index).padStart(2, "0")} ·
            </div>
          )}
        </Reveal>

        <div className="md:col-span-7">
          <div className="space-y-7">
            {paragraphs.map((p, i) => (
              <Reveal
                key={p.slice(0, 32)}
                delay={120 + i * 80}
                className={`text-lg leading-[1.7] md:text-xl md:leading-[1.65] ${textMuted}`}
              >
                <p>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const VALUES = [
  {
    title: "Integrity",
    description: "Always do the right thing — even when it is difficult or costly.",
    supporting:
      "We conduct business with honesty, ethics, and complete transparency, ensuring long-term trust with customers, suppliers, and employees.",
  },
  {
    title: "Fairness",
    description: "Treat everyone with equity and provide true value-for-money.",
    supporting:
      "Whether in pricing, service, or relationships, fairness ensures customers feel protected and respected.",
  },
  {
    title: "Respect & Humility",
    description: "Value every stakeholder and interact with humility.",
    supporting:
      "Respect builds strong relationships with customers, employees, and partners, while humility keeps us grounded as we grow.",
  },
  {
    title: "Commitment & Reliability",
    description: "Keep promises and honour commitments, no matter what.",
    supporting:
      "Reliability in service, delivery, and relationships strengthens confidence in the brand across generations.",
  },
  {
    title: "Loyalty & Discipline",
    description: "Be loyal to the organisation and disciplined in actions.",
    supporting:
      "Discipline in execution and loyalty to the business vision create stability, consistency, and pride in the company culture.",
  },
];

/**
 * Premium value card.
 * - Default: only the value title (large bold)
 * - Desktop hover OR mobile tap: reveals description + supporting text
 * - Smooth lift, soft shadow, orange accent rule on the side
 */
const ValueCard = ({ value, index, expanded, onToggle }) => {
  const num = String(index + 1).padStart(2, "0");
  return (
    <button
      type="button"
      onClick={onToggle}
      onMouseLeave={() => expanded && onToggle()}
      data-testid={`value-card-${index}`}
      aria-expanded={expanded}
      className={`group relative flex h-full w-full flex-col rounded-2xl border bg-white p-7 text-left shadow-lux transition-all duration-500 ease-out md:p-9
        ${expanded ? "-translate-y-1 border-[#e85a1c]/30 shadow-lux-hover" : "border-black/5 hover:-translate-y-1 hover:border-[#e85a1c]/20 hover:shadow-lux-hover"}`}
    >
      {/* Orange accent bar */}
      <span className="absolute left-0 top-7 h-12 w-[3px] rounded-r bg-[#e85a1c] md:top-9" />

      <div className="flex items-center justify-between font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
        <span>/ Value · {num}</span>
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-full border border-[#e85a1c]/30 text-[#e85a1c] transition-transform duration-500 ${
            expanded ? "rotate-45 bg-[#e85a1c]/10" : "group-hover:rotate-45 group-hover:bg-[#e85a1c]/10"
          }`}
          aria-hidden
        >
          +
        </span>
      </div>

      <h3
        className="mt-6 font-display text-[34px] uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-[40px]"
        data-testid={`value-title-${index}`}
      >
        {value.title}
      </h3>

      {/* Reveal: desktop hover OR mobile tap */}
      <div
        data-testid={`value-reveal-${index}`}
        className={`grid transition-all duration-500 ease-out ${
          expanded
            ? "mt-5 grid-rows-[1fr] opacity-100"
            : "mt-0 grid-rows-[0fr] opacity-0 md:group-hover:mt-5 md:group-hover:grid-rows-[1fr] md:group-hover:opacity-100"
        }`}
      >
        <div className="overflow-hidden">
          <p className="font-display-med text-base leading-[1.45] text-[#1c1c1e] md:text-[17px]">
            {value.description}
          </p>
          <p className="mt-3 text-[14px] leading-[1.7] text-[#4a4a4e] md:text-[15px]">
            {value.supporting}
          </p>
        </div>
      </div>

      <div
        className={`mt-auto pt-6 font-mono-industrial text-[10px] uppercase tracking-[0.25em] transition-colors ${
          expanded ? "text-[#e85a1c]" : "text-[#bababd] group-hover:text-[#e85a1c]"
        }`}
      >
        {expanded ? "Tap again to close" : "Hover or tap to reveal"}
      </div>
    </button>
  );
};

export default function Team() {
  const [members, setMembers] = useState([]);
  const [activeMember, setActiveMember] = useState(null);
  const [expandedValue, setExpandedValue] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/team`)
      .then((r) => {
        if (mounted && Array.isArray(r.data)) setMembers(r.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div data-testid="about-team" className="grain bg-[#f7f7f5]">
      <AboutSubnav />
      <AboutHero
        kicker="/ Team · Purpose · Values · Vision · Mission"
        title="The people"
        accent={"\u00A0behind the steel\u2009."}
        subtitle="Three generations, one unchanging principle, and a leadership team that walks every yard."
      />

      {/* 1 · PURPOSE */}
      <EditorialSplit
        testId="team-purpose"
        index={1}
        kicker="/ Purpose"
        headlineTop="WHY WE"
        headlineAccent="EXIST."
        tone="offwhite"
        paragraphs={[
          "Our purpose is to honour the legacy of our founder by building a business rooted in trust, fairness, and hard work — providing quality steel that helps families and industries create lasting foundations.",
          "Beyond profits, we exist to support society through reliable service, fair value, and opportunities that uplift employees, partners, and communities.",
        ]}
      />

      {/* 2 · VALUES — premium interactive card grid */}
      <section
        data-testid="team-values-wrap"
        aria-label="Values"
        className="border-b border-black/5 bg-white py-24 md:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Values
            </div>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-tighter text-[#1c1c1e] md:text-7xl">
              Five<br />
              <span className="text-[#e85a1c]">non-negotiables.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4a4a4e] md:text-lg">
              These five principles guide every weighbridge, every dispatch, and every conversation we have.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal
                key={v.title}
                delay={i * 90}
                className="contents"
              >
                <div className="h-full">
                  <ValueCard
                    value={v}
                    index={i}
                    expanded={expandedValue === i}
                    onToggle={() => setExpandedValue((x) => (x === i ? null : i))}
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · VISION */}
      <EditorialSplit
        testId="team-vision"
        index={3}
        kicker="/ Vision · Next 10 Years"
        headlineTop="WHERE WE'RE"
        headlineAccent="HEADED."
        tone="dark"
        paragraphs={[
          "In the next ten years, we aim to be South India's most trusted and respected steel trading company — renowned for integrity, quality, and fairness.",
          "We will expand our reach, deliver value-added services beyond the rod, and create a thriving workplace where employees, customers, and partners grow together.",
        ]}
      />

      {/* 4 · MISSION */}
      <EditorialSplit
        testId="team-mission"
        index={4}
        kicker="/ Mission"
        headlineTop="STEEL BUYING,"
        headlineAccent="SIMPLIFIED."
        tone="offwhite"
        paragraphs={[
          "Make steel purchasing simple, fair, and reliable for homeowners, IHBs, builders, and industries — through quality products, expert guidance, and ethical service.",
          "We create value for all stakeholders through trust, transparency, and operational excellence — while building a legacy of integrity and goodwill.",
        ]}
      />

      {/* 5 · MESSAGE FROM FOUNDER */}
      <section
        data-testid="team-founder"
        className="border-b border-black/5 bg-white py-24 md:py-32"
      >
        <div className="mx-auto grid max-w-[1400px] gap-y-14 gap-x-16 px-6 md:grid-cols-12 lg:px-10">
          <Reveal className="md:col-span-5">
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-[#efefe9] shadow-lux">
              <img
                src="https://customer-assets.emergentagent.com/job_steel-3d-portal/artifacts/c6ulvwnv_20210105_104006000_iOS%201.jpg.jpg"
                alt="late Shri Motilal Baheti — Founder"
                data-testid="founder-portrait"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-5 text-white">
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-white/70">
                  Founder · 1969
                </div>
                <div className="mt-1 font-display-med text-lg uppercase tracking-tight">
                  late Shri Motilal Baheti
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={150} className="md:col-span-7">
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Message from the founder
            </div>
            <h2 className="mt-6 font-display text-5xl uppercase leading-[0.86] tracking-tighter text-[#1c1c1e] md:text-7xl">
              A NAME ETCHED INTO<br />
              <span className="text-[#e85a1c]">HYDERABAD&rsquo;S SKYLINE.</span>
            </h2>

            <div className="mt-10 rounded-2xl border-l-4 border-[#e85a1c] bg-[#fff8f3] p-7 md:p-9">
              <Quote size={28} className="text-[#e85a1c]" />
              <blockquote className="mt-4 font-display text-2xl uppercase leading-[1.1] tracking-tight text-[#1c1c1e] md:text-3xl">
                &ldquo;Steel holds the building,
                <br />
                trust holds the business.&rdquo;
              </blockquote>
              <cite className="mt-5 block font-mono-industrial text-[10px] not-italic uppercase tracking-[0.25em] text-[#8b8b8f]">
                — late Shri Motilal Baheti · Founder
              </cite>
            </div>

            <div className="mt-9 space-y-5 text-lg leading-[1.7] text-[#4a4a4e]">
              <p>
                When the founder opened the shutters at Esamiya Bazar over five decades ago, he established one guiding principle — <em>&ldquo;the weighbridge does not lie, and neither do we.&rdquo;</em> That principle has remained unchanged ever since.
              </p>
              <p>
                The business was founded in <strong>1969</strong> by <strong>late Shri Motilal Baheti</strong>, whose passion for construction inspired his entry into the industry. Today, Baheti Steel Traders stands among the leading suppliers in Telangana — offering high-quality TMT products and a range of ancillary services at highly competitive prices.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6 · MEET THE TEAM */}
      <section
        data-testid="team-meet"
        className="border-b border-black/5 bg-[#f7f7f5] py-24 md:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Meet the team
            </div>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.88] tracking-tighter text-[#1c1c1e] md:text-7xl">
              The people behind<br />
              <span className="text-[#e85a1c]">the success.</span>
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {members.map((m, i) => {
              const initials =
                m.initials ||
                m.name.split(" ").map((n) => n[0]).join("").slice(0, 2);
              return (
                <Reveal
                  key={m.id || m.name}
                  delay={i * 80}
                  className="contents"
                >
                  <button
                    type="button"
                    onClick={() => setActiveMember(m)}
                    data-testid={`team-member-${i}`}
                    aria-label={`Open profile · ${m.name}`}
                    className="group relative flex w-full cursor-pointer flex-col rounded-2xl border border-black/5 bg-white p-6 text-left shadow-lux transition-all duration-300 ease-out hover:-translate-y-1.5 hover:border-[#e85a1c]/20 hover:shadow-lux-hover focus:outline-none focus:ring-2 focus:ring-[#e85a1c]/40"
                  >
                    {/* Image / initials — no grey frame, seamless */}
                    <div className="relative mx-auto h-28 w-28 overflow-hidden rounded-full">
                      {m.image_url ? (
                        <img
                          src={m.image_url}
                          alt={m.name}
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#e85a1c] to-[#c64812] font-display text-4xl tracking-tighter text-white transition-transform duration-500 ease-out group-hover:scale-105">
                          {initials}
                        </div>
                      )}
                    </div>

                    <div className="mt-5 text-center font-display-med text-lg uppercase tracking-tight text-[#1c1c1e]">
                      {m.name}
                    </div>
                    <div className="mt-1 text-center font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#e85a1c]">
                      {m.role}
                    </div>
                    <p className="mt-3 text-center text-sm leading-relaxed text-[#4a4a4e]">
                      {m.bio}
                    </p>

                    <div className="mt-5 flex items-center justify-center font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#bababd] transition-colors group-hover:text-[#e85a1c]">
                      <span>View profile</span>
                      <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">
                        →
                      </span>
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {activeMember && (
        <TeamMemberModal
          member={activeMember}
          onClose={() => setActiveMember(null)}
        />
      )}
    </div>
  );
}
